"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ChevronsUpDown, ImageIcon, Loader2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { HexColorPicker } from "react-colorful";
import { cn } from "@/lib/utils";
import { CountryCodes } from "@/utils/country-codes";
import { Timezones } from "@/utils/timezones";
import { LogoUploadValidations } from "@/components/common/file-picker/upload-validations";
import { uploadFileWithValidation } from "@/components/common/file-picker/upload-with-validation";
import { SDK } from "@/@sdk";
import type { iOrganization } from "@/@sdk/user-management/user-management.models";

export const brandBasicsSchema = z.object({
  name: z.string().min(1, "Brand name is required"),
  homeCurrency: z.string().min(1, "Home currency is required"),
  website: z.string().url("Invalid URL").optional().or(z.literal("")),
  country: z.string().optional(),
  timezone: z.string().optional(),
  primaryColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Invalid color code"),
  logo: z.string().optional(),
});

export type BrandBasicsFormValues = z.infer<typeof brandBasicsSchema>;

const CURRENCY_OPTIONS = ["GBP", "USD", "EUR", "INR", "LKR"] as const;

export function buildBrandBasicsPrefillFromWorkspace(
  organization: Pick<iOrganization, "data"> | null | undefined
): BrandBasicsFormValues {
  const data = organization?.data;
  return {
    name: data?.name || "",
    website: data?.website || "",
    country: data?.country || "",
    timezone: data?.timezone || "",
    primaryColor: data?.primaryColor || "#000000",
    logo: data?.logo || "",
    homeCurrency: data?.primaryCurrency || "GBP",
  };
}

type BrandBasicsFormProps = {
  organizationId?: string;
  defaultValues: BrandBasicsFormValues;
  homeCurrencyLocked?: boolean;
  mode?: "settings" | "wizard";
  isSubmitting?: boolean;
  onSubmit: (values: BrandBasicsFormValues) => Promise<void>;
};

export function BrandBasicsForm({
  organizationId,
  defaultValues,
  homeCurrencyLocked = false,
  mode = "wizard",
  isSubmitting = false,
  onSubmit,
}: BrandBasicsFormProps) {
  const [openCountry, setOpenCountry] = useState(false);
  const [openTimezone, setOpenTimezone] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<BrandBasicsFormValues>({
    resolver: zodResolver(brandBasicsSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const countryOptions = useMemo(
    () =>
      CountryCodes.map((country) => ({
        label: country.name,
        value: country.code,
      })),
    []
  );

  const timezoneOptions = useMemo(
    () =>
      Timezones.map((timezone) => ({
        label: timezone,
        value: timezone,
      })),
    []
  );

  const logoValue = form.watch("logo");

  const uploadLogo = useCallback(
    (file: File) => {
      if (!organizationId) return;
      setIsUploading(true);
      uploadFileWithValidation({
        file,
        reference: { type: "ORGANIZATION", entityId: organizationId },
        uploadFileFunction: (uploadFile: File) =>
          SDK.uploadFile(
            uploadFile,
            { type: "ORGANIZATION", entityId: organizationId },
            []
          ),
        validations: LogoUploadValidations,
        resizeBeforeUpload: {
          maxWidth: 1080,
          maxHeight: 1080,
          compressFormat: "PNG",
        },
      })
        .then((uploaded) => form.setValue("logo", uploaded.url))
        .finally(() => setIsUploading(false));
    },
    [form, organizationId]
  );

  return (
    <Form {...form}>
      <form
        id="brand-basics-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Brand name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Flight Centre Premium" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="homeCurrency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Home currency</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={homeCurrencyLocked || isSubmitting}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Cannot be changed after the brand is created.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="logo"
          render={() => (
            <FormItem>
              <FormLabel>Logo</FormLabel>
              <div className="flex items-center gap-4">
                {logoValue ? (
                  <img
                    src={logoValue}
                    alt="Brand logo"
                    className="h-14 w-14 rounded border object-contain p-1"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded border bg-muted">
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="brand-basics-logo"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadLogo(file);
                    e.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploading || !organizationId}
                  onClick={() =>
                    document.getElementById("brand-basics-logo")?.click()
                  }
                >
                  {isUploading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Upload logo
                </Button>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="primaryColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Brand colour</FormLabel>
              <div className="flex items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="h-10 w-10 rounded-md border"
                      style={{ backgroundColor: field.value }}
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3">
                    <HexColorPicker color={field.value} onChange={field.onChange} />
                  </PopoverContent>
                </Popover>
                <Input {...field} className="w-[120px] font-mono" />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <Popover open={openCountry} onOpenChange={setOpenCountry}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "w-full justify-between font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value
                          ? countryOptions.find((c) => c.value === field.value)
                              ?.label
                          : "Select country"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-[min(400px,90vw)] p-0">
                    <Command>
                      <CommandInput placeholder="Search country..." />
                      <CommandEmpty>No country found.</CommandEmpty>
                      <CommandGroup className="max-h-60 overflow-auto">
                        {countryOptions.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.label}
                            onSelect={() => {
                              field.onChange(option.value);
                              setOpenCountry(false);
                            }}
                          >
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reporting timezone</FormLabel>
                <Popover open={openTimezone} onOpenChange={setOpenTimezone}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "w-full justify-between font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value || "Select timezone"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-[min(400px,90vw)] p-0">
                    <Command>
                      <CommandInput placeholder="Search timezone..." />
                      <CommandEmpty>No timezone found.</CommandEmpty>
                      <CommandGroup className="max-h-60 overflow-auto">
                        {timezoneOptions.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.label}
                            onSelect={() => {
                              field.onChange(option.value);
                              setOpenTimezone(false);
                            }}
                          >
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {mode === "settings" ? (
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting || isUploading}>
              Save
            </Button>
          </div>
        ) : null}
      </form>
    </Form>
  );
}
